import styled from 'styled-components/macro';

export const Container = styled.div`
  min-height: 100vh;
`;

export const HeaderBanner = styled.header`
  background: #fff;
  border-bottom: 4px solid ${({ theme }) => theme.colors.secondary};
  box-shadow: 0 4px 18px rgba(45, 47, 143, 0.08);
`;

export const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  max-width: 1200px;
  min-height: 96px;
  margin: 0 auto;
  padding: 18px 15px;
  box-sizing: border-box;
`;

export const Logo = styled.img`
  display: block;
  width: min(360px, 82vw);
  height: auto;
`;

export const LanguageBar = styled.div`
  display: flex;
  justify-content: flex-end;
  max-width: 1200px;
  margin: 18px auto 0;
  padding: 0 15px;
  box-sizing: border-box;

  @media only screen and (max-width: ${({ theme: { breakpoints } }) =>
      breakpoints.tablet}) {
    justify-content: center;
  }
`;

export const LanguageGroup = styled.div`
  display: inline-flex;
  padding: 4px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 22px;
  background: #fff;
  box-shadow: 0 6px 14px rgba(45, 47, 143, 0.08);
`;

interface ILanguageButton {
  isSelected: boolean;
}

export const LanguageButton = styled.button<ILanguageButton>`
  min-width: 54px;
  height: 36px;
  border: 0;
  border-radius: 18px;
  background: ${({ theme, isSelected }) =>
    isSelected ? theme.colors.primary : '#fff'};
  color: ${({ theme, isSelected }) =>
    isSelected ? '#fff' : theme.colors.primary};
  cursor: pointer;
  font-weight: 700;

  &:focus-visible {
    outline: 3px solid ${({ theme }) => theme.colors.secondary};
    outline-offset: 2px;
  }

  &:hover {
    color: ${({ theme, isSelected }) =>
      isSelected ? '#fff' : theme.colors.secondary};
  }
`;

export const AccountBar = styled.div`
  display: flex;
  justify-content: flex-end;
  max-width: 1200px;
  margin: 12px auto 0;
  padding: 0 15px;
  box-sizing: border-box;

  @media only screen and (max-width: ${({ theme: { breakpoints } }) =>
      breakpoints.tablet}) {
    justify-content: center;
  }
`;

export const LoginForm = styled.form`
  display: grid;
  grid-template-columns: minmax(170px, 240px) minmax(150px, 220px) auto;
  gap: 0;
  align-items: stretch;
  overflow: hidden;
  max-width: 100%;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 8px 20px rgba(45, 47, 143, 0.08);

  @media only screen and (max-width: ${({ theme: { breakpoints } }) =>
      breakpoints.tablet}) {
    width: min(100%, 430px);
    grid-template-columns: 1fr auto;
  }
`;

export const LoginField = styled.div`
  min-width: 0;
  border-right: 1px solid ${({ theme }) => theme.colors.border};

  @media only screen and (max-width: ${({ theme: { breakpoints } }) =>
      breakpoints.tablet}) {
    &:first-child {
      grid-column: 1 / -1;
      border-right: 0;
      border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    }
  }
`;

export const LoginLabel = styled.label`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
`;

export const LoginInput = styled.input`
  width: 100%;
  height: 44px;
  border: 0;
  box-sizing: border-box;
  color: ${({ theme }) => theme.colors.text};
  font-size: 1rem;
  padding: 0 16px;

  &::placeholder {
    color: ${({ theme }) => theme.colors.muted};
  }

  &:focus {
    outline: 3px solid rgba(238, 39, 55, 0.18);
    outline-offset: -3px;
  }
`;

export const SignInButton = styled.button`
  min-width: 92px;
  height: 44px;
  border: 0;
  color: #fff;
  background: ${({ theme }) => theme.colors.primary};
  cursor: pointer;
  font-weight: 800;
  padding: 0 18px;

  &:hover {
    background: ${({ theme }) => theme.colors.secondary};
  }

  &:focus-visible {
    outline: 3px solid ${({ theme }) => theme.colors.secondary};
    outline-offset: -3px;
  }
`;

export const AccountControls = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  flex-wrap: wrap;

  @media only screen and (max-width: ${({ theme: { breakpoints } }) =>
      breakpoints.tablet}) {
    justify-content: center;
  }
`;

interface IAccountButton {
  isSelected?: boolean;
}

export const AccountButton = styled.button<IAccountButton>`
  min-height: 36px;
  border: 1px solid
    ${({ theme, isSelected }) =>
      isSelected ? theme.colors.primary : theme.colors.border};
  border-radius: 4px;
  color: ${({ theme, isSelected }) =>
    isSelected ? '#fff' : theme.colors.primary};
  background: ${({ theme, isSelected }) =>
    isSelected ? theme.colors.primary : '#fff'};
  cursor: pointer;
  font-weight: 700;
  padding: 0 14px;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }
`;

export const AccountError = styled.span`
  align-self: center;
  color: ${({ theme }) => theme.colors.secondary};
  font-weight: 700;
`;

interface ITwoColumnGrid {
  $isSingleColumn?: boolean;
}

export const TwoColumnGrid = styled.main<ITwoColumnGrid>`
  display: grid;
  grid-template-columns: 1fr;
  max-width: 1200px;
  margin: 14px auto auto;
  background: #fff;
  border: 1px solid ${({ theme }) => theme.colors.border};
  box-shadow: 0 10px 30px rgba(45, 47, 143, 0.06);

  @media only screen and (min-width: ${({ theme: { breakpoints } }) =>
      breakpoints.tablet}) {
    grid-template-columns: ${({ $isSingleColumn }) =>
      $isSingleColumn ? '1fr' : '1fr 4fr'};
    margin-top: 18px;
  }
`;

export const Side = styled.div`
  display: grid;
  justify-content: center;
  padding: 15px;
  box-sizing: border-box;

  @media only screen and (min-width: ${({ theme: { breakpoints } }) =>
      breakpoints.tablet}) {
    align-content: baseline;
  }
`;

export const Main = styled.main`
  min-width: 0;
`;

export const MainHeader = styled.main`
  display: flex;
  align-items: center;
  min-height: 56px;
  padding: 0 45px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};

  p {
    margin: 0;
    font-size: 1.25rem;
  }
`;

export const SearchBar = styled.div`
  padding: 16px 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  box-sizing: border-box;
  max-width: 100%;
  overflow: hidden;
`;

export const SearchLabel = styled.label`
  display: block;
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 700;
  margin: 0 45px 8px;
`;

export const SearchInput = styled.input`
  display: block;
  width: calc(100% - 90px);
  max-width: calc(100% - 90px);
  height: 42px;
  margin: 0 45px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 4px;
  box-sizing: border-box;
  color: ${({ theme }) => theme.colors.text};
  font-size: 1rem;
  padding: 0 12px;

  &::placeholder {
    color: ${({ theme }) => theme.colors.muted};
  }

  &:focus {
    outline: 3px solid rgba(238, 39, 55, 0.2);
    border-color: ${({ theme }) => theme.colors.secondary};
  }
`;

export const EmptyState = styled.p`
  margin: 24px 45px;
  color: ${({ theme }) => theme.colors.muted};
  font-weight: 700;
`;

export const OrderHistory = styled.div`
  padding: 18px 45px 36px;
`;

export const OrderHistoryItem = styled.div`
  display: grid;
  grid-template-columns: 80px 1fr auto auto auto auto;
  gap: 14px;
  align-items: center;
  padding: 14px 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};

  strong {
    color: ${({ theme }) => theme.colors.primary};
  }

  @media only screen and (max-width: ${({ theme: { breakpoints } }) =>
      breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: 4px;
  }
`;

export const ViewDetailsButton = styled.button`
  min-height: 34px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 4px;
  color: ${({ theme }) => theme.colors.primary};
  background: #fff;
  cursor: pointer;
  font-weight: 700;
  padding: 0 12px;

  &:focus-visible {
    outline: 3px solid ${({ theme }) => theme.colors.secondary};
    outline-offset: 2px;
  }
`;

export const ReorderButton = styled.button`
  min-height: 34px;
  border: 1px solid ${({ theme }) => theme.colors.primary};
  border-radius: 4px;
  color: #fff;
  background: ${({ theme }) => theme.colors.primary};
  cursor: pointer;
  font-weight: 700;
  padding: 0 12px;

  &:focus-visible {
    outline: 3px solid ${({ theme }) => theme.colors.secondary};
    outline-offset: 2px;
  }
`;

export const OrderDetail = styled.article`
  display: grid;
  gap: 18px;
`;

export const OrderDetailHeader = styled.header`
  display: grid;
  gap: 10px;
  padding-bottom: 18px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

export const OrderBackButton = styled.button`
  justify-self: start;
  min-height: 34px;
  border: 0;
  color: ${({ theme }) => theme.colors.primary};
  background: transparent;
  cursor: pointer;
  font-weight: 800;
  padding: 0;

  &:focus-visible {
    outline: 3px solid ${({ theme }) => theme.colors.secondary};
    outline-offset: 2px;
  }
`;

export const OrderDetailTitle = styled.h3`
  margin: 0;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 1.35rem;
`;

export const OrderDetailMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px 18px;
  color: ${({ theme }) => theme.colors.muted};
  font-weight: 700;
`;

export const OrderNote = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.text};
`;

export const OrderDetailGroups = styled.div`
  display: grid;
  gap: 18px;
`;

export const OrderDetailGroup = styled.section`
  display: grid;
  gap: 10px;
`;

export const OrderDetailCategory = styled.h4`
  margin: 0;
  color: ${({ theme }) => theme.colors.secondary};
  font-size: 1rem;
`;

export const OrderDetailItem = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 12px;
  align-items: start;
  padding: 12px 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};

  @media only screen and (max-width: ${({ theme: { breakpoints } }) =>
      breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: 6px;
  }
`;

export const OrderDetailItemName = styled.span`
  color: ${({ theme }) => theme.colors.text};
  font-weight: 700;
`;

export const OrderDetailItemMeta = styled.span`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
  color: ${({ theme }) => theme.colors.muted};

  @media only screen and (max-width: ${({ theme: { breakpoints } }) =>
      breakpoints.tablet}) {
    justify-content: flex-start;
  }
`;
